import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, deleteImage, removeBackgroundImage } from '../apiClient';
import { SessionAuth } from 'supertokens-auth-react/recipe/session';
import SimpleImageCard from './SimpleImageCard';
import SelectableImageCard from './SelectableImageCard';

const Gallery = () => {
  const location = useLocation();
  const { selectedProduct } = location.state || {};
  const navigate = useNavigate();

  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImages, setSelectedImages] = useState(new Set());
  const [processing, setProcessing] = useState(false);

  const handleImageSelect = (image) => {
    if (selectedProduct) {
      navigate('/configure-mockup', { state: { selectedProduct: selectedProduct, selectedImage: image } });
    } else {
      navigate('/products', { state: { selectedImage: image } });
    }
  };

  const toggleImageSelection = (imageId) => {
    setSelectedImages(prevSelectedImages => {
      const newSelectedImages = new Set(prevSelectedImages);
      if (newSelectedImages.has(imageId)) {
        newSelectedImages.delete(imageId);
      } else {
        newSelectedImages.add(imageId);
      }
      return newSelectedImages;
    });
  };

  const handleBulkDelete = async () => {
    if (window.confirm(`Are you sure you want to delete ${selectedImages.size} images? This action cannot be undone.`)) {
      try {
        await Promise.all(Array.from(selectedImages).map(id => deleteImage(id)));
        setImages(images.filter((img) => !selectedImages.has(img.id)));
        setSelectedImages(new Set());
      } catch (err) {
        console.error('Error deleting images:', err);
        setError('Failed to delete images. Please try again later.');
      }
    }
  };

  const handleBulkRemoveBackground = async () => {
    try {
      setProcessing(true);
      const updatedImages = await Promise.all(images.map(async (image) => {
        if (selectedImages.has(image.id)) {
          try {
            const response = await removeBackgroundImage(image.id);
            console.log('Response from removeBackgroundImage:', response);
            // Use image_url from the response, fallback if needed
            const newImageUrl = response.image_url || response.imageUrl || response.url;
            if (newImageUrl) {
              const newImageUrlWithCacheBuster = `${newImageUrl}?_=${new Date().getTime()}`;
              return { ...image, image_url: newImageUrlWithCacheBuster };
            }
          } catch (e) {
            console.error(`Failed to remove background for image ${image.id}`, e);
            // Return original image if failure, or maybe mark it as failed? 
            // For now, keeping original allows retry.
          }
        }
        return image;
      }));
      setImages(updatedImages);
      setSelectedImages(new Set());
    } catch (err) {
      console.error('Error removing backgrounds:', err);
      setError('Failed to remove backgrounds. Please try again later.');
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    const fetchImages = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get('/gallery');
        const processedImages = response.data.map((image) => ({ ...image, id: image.id }));
        setImages(processedImages);
      } catch (err) {
        console.error('Error fetching gallery images:', err);
        setError('Failed to load images. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, []);

  return (
    <SessionAuth>
      <div className="container mx-auto p-6 bg-card rounded-lg shadow-lg tour-gallery">
        <h1 className="text-3xl font-bold mb-8 text-center text-gray-800">Your Image Gallery</h1>
        {selectedProduct ? (
          <div className="mb-6 p-4 border border-primary rounded-lg bg-blue-50">
            <h2 className="text-xl font-semibold text-gray-800">Selected Product: {selectedProduct.name}</h2>
            <p className="text-gray-700">Now, select an image to place on this product.</p>
          </div>
        ) : (
          <p className="text-center text-gray-600 mb-10">Select an image to choose a product to print it on.</p>
        )}

        {images.length > 0 && (
          <div className="flex justify-end mb-6">
            <button
              onClick={handleBulkRemoveBackground}
              disabled={selectedImages.size === 0 || processing}
              className="bg-primary hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md mr-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {processing ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : 'Remove Background'}
            </button>
            <button onClick={handleBulkDelete} disabled={selectedImages.size === 0 || processing} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-md disabled:opacity-50 disabled:cursor-not-allowed">
              Delete
            </button>
          </div>
        )}

        {loading ? (
          <p className="text-center text-gray-600">Loading images...</p>
        ) : error ? (
          <p className="text-center text-red-500">{error}</p>
        ) : images.length === 0 ? (
          <p className="text-center text-gray-600">No images generated yet. Go to the main page to create some!</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
            {images.map((image) => (
              selectedProduct ? (
                <SelectableImageCard key={image.id} image={image} onSelect={handleImageSelect} onToggleSelection={toggleImageSelection} isSelected={selectedImages.has(image.id)} />
              ) : (
                <SimpleImageCard key={image.id} image={image} onClick={handleImageSelect} onToggleSelection={toggleImageSelection} isSelected={selectedImages.has(image.id)} />
              )
            ))}
          </div>
        )}
      </div>
    </SessionAuth>
  );
};

export default Gallery;
